import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { formatCurrency, eurosToCents, centsToEuros } from '@/lib/utils';
import { differenceInMonths, parseISO } from 'date-fns';

interface HoldingCostsInputProps {
  startDate: string | null;
  endDate: string | null;
  monthlyTotal: number | null;
  details: {
    financing: number | null;
    utilities: number | null;
    insurance: number | null;
    other: number | null;
  };
  onChange: (values: {
    startDate: string | null;
    endDate: string | null;
    monthlyTotal: number | null;
    details: HoldingCostsInputProps['details'];
  }) => void;
}

export function HoldingCostsInput({
  startDate,
  endDate,
  monthlyTotal,
  details,
  onChange,
}: HoldingCostsInputProps) {
  const [showDetails, setShowDetails] = useState(false);

  // Berechne Monate
  const months = startDate
    ? differenceInMonths(
        endDate ? parseISO(endDate) : new Date(),
        parseISO(startDate)
      ) || 1
    : 0;

  // Berechne monatliche Summe aus Details
  const calculatedMonthly =
    (details.financing || 0) +
    (details.utilities || 0) +
    (details.insurance || 0) +
    (details.other || 0);

  const hasDetails = calculatedMonthly > 0;
  const displayMonthly = hasDetails ? calculatedMonthly : monthlyTotal;
  const totalHoldingCosts = (displayMonthly || 0) * (months > 0 ? months : 1);

  return (
    <Card className="p-4 space-y-4 bg-background border-border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-foreground-muted" />
          <h4 className="font-medium">Haltekosten</h4>
        </div>
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
        >
          {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showDetails ? 'Weniger' : 'Details'}
        </button>
      </div>

      <p className="text-xs text-foreground-muted">
        Kosten während Leerstand oder Sanierung (bevor Mieteinnahmen fließen)
      </p>

      {/* Zeitraum */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Von</Label>
          <Input
            type="date"
            value={startDate || ''}
            onChange={(e) => onChange({ startDate: e.target.value || null, endDate, monthlyTotal, details })}
            className="h-9 text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Bis (leer = laufend)</Label>
          <Input
            type="date"
            value={endDate || ''}
            onChange={(e) => onChange({ startDate, endDate: e.target.value || null, monthlyTotal, details })}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Dauer anzeigen */}
      {startDate && (
        <p className="text-sm text-foreground-muted">
          Dauer: {months} {months === 1 ? 'Monat' : 'Monate'}
          {!endDate && ' (laufend)'}
        </p>
      )}

      {/* Monatliche Kosten */}
      {!showDetails ? (
        <div className="space-y-1">
          <Label className="text-xs">Monatliche Kosten</Label>
          <div className="relative">
            <Input
              type="number"
              step="100"
              value={centsToEuros(displayMonthly) || ''}
              onChange={(e) => onChange({
                startDate, endDate,
                monthlyTotal: eurosToCents(parseFloat(e.target.value) || 0),
                details,
              })}
              placeholder="0"
              disabled={hasDetails}
              className="h-9"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted text-sm">
              €/Monat
            </span>
          </div>
        </div>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-2 overflow-hidden"
          >
            {[
              { key: 'financing', label: 'Finanzierung (Zins+Tilgung)' },
              { key: 'utilities', label: 'Nebenkosten/Hausgeld' },
              { key: 'insurance', label: 'Versicherung' },
              { key: 'other', label: 'Sonstiges (Strom etc.)' },
            ].map((item) => (
              <div key={item.key} className="space-y-1">
                <Label className="text-xs text-foreground-muted">{item.label}</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="50"
                    value={centsToEuros(details[item.key as keyof typeof details]) || ''}
                    onChange={(e) => onChange({
                      startDate, endDate, monthlyTotal,
                      details: {
                        ...details,
                        [item.key]: eurosToCents(parseFloat(e.target.value) || 0),
                      },
                    })}
                    placeholder="0"
                    className="h-9 text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted text-xs">
                    €/Mo
                  </span>
                </div>
              </div>
            ))}

            <div className="pt-2 flex justify-between items-center text-sm">
              <span className="text-foreground-muted">Summe monatlich</span>
              <span className="font-medium">{formatCurrency(calculatedMonthly)}/Monat</span>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Total */}
      {startDate && (displayMonthly || 0) > 0 && (
        <div className="pt-3 border-t border-border flex justify-between items-center">
          <span className="text-sm text-foreground-muted">Haltekosten gesamt</span>
          <span className="font-semibold text-negative">{formatCurrency(totalHoldingCosts)}</span>
        </div>
      )}
    </Card>
  );
}
